import { useApolloClient, useMutation } from '@apollo/client';
import { END_SUPPORT_SESSION } from '@employ-engage/common-components';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTime } from './support-access-prompt';
import {
  useSupportSession,
  useVisiblePollInterval,
} from './use-support-session';

// Also how quickly the admin is sent back after the user ends access. Any
// action they try in the meantime is refused by the API regardless.
const POLL_INTERVAL_MS = 60 * 1000;

/**
 * Shown to a super admin while they're acting as a user. Exiting — or access
 * running out or being ended by the user — hands the browser back to the
 * admin's own session and returns them to the portal.
 */
export const SupportSessionBanner = () => {
  const { t } = useTranslation(['common']);
  const client = useApolloClient();
  const { supportSession } = useSupportSession({
    pollInterval: useVisiblePollInterval(POLL_INTERVAL_MS),
  });
  const [endSupportSession] = useMutation(END_SUPPORT_SESSION);
  const exitingRef = useRef(false);

  const exit = useCallback(async () => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    try {
      const { data } = await endSupportSession();
      await client.clearStore();
      window.location.assign(data?.endSupportSession?.redirectUrl || '/');
    } catch {
      window.location.assign('/');
    }
  }, [client, endSupportSession]);

  useEffect(() => {
    if (!supportSession) return;
    if (!supportSession.isActive) {
      exit();
      return;
    }
    if (!supportSession.accessExpiresAt) return;
    const remaining =
      new Date(supportSession.accessExpiresAt).getTime() - Date.now();
    const timer = setTimeout(exit, Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [supportSession, exit]);

  if (!supportSession) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-fit max-w-[90vw] items-center gap-3 rounded-full bg-amber-400 px-4 py-2 text-sm font-medium text-gray-900 shadow-lg"
    >
      <span>
        {t('support-session-banner', {
          email: supportSession.targetEmail,
          time: formatTime(supportSession.accessExpiresAt),
        })}
      </span>
      <button
        type="button"
        onClick={exit}
        className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white hover:bg-gray-700"
      >
        {t('Exit')}
      </button>
    </div>
  );
};

export default SupportSessionBanner;
